import { AppIcon } from '@/components/ui/app-icon';
import { useSidebar } from '@/components/sidebar/sidebar-provider.component';
import { DynamicIcon } from '@/components/ui/icons/dynamic-icon';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useTheme } from '@/hooks/use-theme.hook';
import { iconSpinnerThird, iconUser } from '@/lib/icons';
import { cn } from '@/lib/utils';
import {
  useClerk,
  useOrganization,
  useOrganizationList,
  useUser,
} from '@clerk/react';
import type {
  OrganizationSuggestionResource,
  UserOrganizationInvitationResource,
} from '@clerk/react/types';
import { Link } from '@tanstack/react-router';
import {
  useEffect,
  useState,
  type ComponentPropsWithRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

/**
 * The account menu in the tabbed sidebar's footer, as a themed Popover that
 * opens to the RIGHT (the folded sidebar is only 64px wide, so an up/down
 * popover would clip). The avatar comes from `useUser()`; the menu holds
 * switching, joining and creating an organization, a link to Settings, the
 * resources and links, and Log out. Settings holds the rest.
 */

type UserMenuRowProps = {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  to?: string;
  external?: boolean;
  destructive?: boolean;
  isDark: boolean;
  /** At the row's right edge, e.g. a disclosure chevron. */
  trailing?: ReactNode;
  /** For a row that shows and hides what follows it. */
  expanded?: boolean;
} & Omit<ComponentPropsWithRef<'button'>, 'onClick' | 'children' | 'className'>;

/** The row look shared by every entry, themed to the popover surface. */
const menuRowClass = (isDark: boolean, destructive?: boolean) =>
  cn(
    'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm whitespace-nowrap transition-colors',
    destructive
      ? isDark
        ? 'text-rose-300 hover:!bg-rose-500/10'
        : 'text-rose-600 hover:bg-rose-500/10'
      : isDark
        ? 'text-slate-300 hover:!bg-white/[0.08]'
        : 'text-muted-foreground hover:bg-black/[0.05]'
  );

function UserMenuRow({
  icon,
  label,
  onClick,
  to,
  external,
  destructive,
  isDark,
  trailing,
  expanded,
  // A button row's ref and props, as a popover trigger passes them.
  ...button
}: UserMenuRowProps) {
  // Match the sidebar item hover (tertiary text -> muted on hover), themed to
  // the dark popover surface like the Clerk UserButton popover action buttons.
  const rowClass = menuRowClass(isDark, destructive);

  const iconClass = cn(
    'flex w-4 shrink-0 items-center justify-center',
    destructive
      ? isDark
        ? 'text-rose-400'
        : 'text-rose-500'
      : isDark
        ? 'text-slate-400'
        : 'text-tertiary-foreground'
  );

  const inner = (
    <>
      <span className={iconClass}>{icon}</span>
      <span className='truncate'>{label}</span>
      {trailing}
    </>
  );

  if (to && external) {
    return (
      <a
        href={to}
        target='_blank'
        rel='noopener noreferrer'
        onClick={onClick}
        className={rowClass}
      >
        {inner}
      </a>
    );
  }

  if (to) {
    return (
      <Link to={to} onClick={onClick} className={rowClass}>
        {inner}
      </Link>
    );
  }

  return (
    <button
      type='button'
      aria-expanded={expanded}
      {...button}
      onClick={onClick}
      className={rowClass}
    >
      {inner}
    </button>
  );
}

/** An organization's mark: its Clerk image, else its initial. */
function OrganizationMark({
  name,
  imageUrl,
}: {
  name: string;
  imageUrl?: string;
}) {
  return imageUrl ? (
    <img
      src={imageUrl}
      alt=''
      className='h-4 w-4 shrink-0 rounded object-cover'
    />
  ) : (
    <span
      aria-hidden
      className='grid h-4 w-4 shrink-0 place-items-center rounded bg-muted text-[10px] font-medium text-muted-foreground'
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * The tabbed sidebar's organization rows: the switch, then the invitations
 * and suggestions Clerk's switcher listed. Joining an invitation opens the
 * switch's list, where the organization just joined now is.
 */
function OrganizationSection({
  activeId,
  isDark,
  onSwitched,
}: {
  activeId: string | undefined;
  isDark: boolean;
  onSwitched: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <OrganizationSwitch
        activeId={activeId}
        isDark={isDark}
        onSwitched={onSwitched}
        expanded={expanded}
        onExpandedChange={setExpanded}
      />
      <OrganizationInvitations
        isDark={isDark}
        onJoined={() => setExpanded(true)}
      />
    </>
  );
}

/**
 * The tabbed sidebar's organization switch: one row that shows the user's
 * organizations beneath it, the active one checked. Picking another makes it
 * active the way Clerk's switcher did here (setActive with no redirect, since
 * the switcher never set afterSelectOrganizationUrl), and the app follows the
 * active organization from there. Hidden when the user has one organization.
 */
function OrganizationSwitch({
  activeId,
  isDark,
  onSwitched,
  expanded,
  onExpandedChange,
}: {
  activeId: string | undefined;
  isDark: boolean;
  onSwitched: () => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}) {
  const { isLoaded, setActive, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const [switchingId, setSwitchingId] = useState<string | null>(null);

  // Creating, leaving or deleting an organization happens in Clerk's own
  // modals, whose cache is not the app's, so the list is reloaded each time
  // the menu opens and whenever the active organization changes. revalidate
  // is a new function every render, so it stays out of the deps.
  const revalidateMemberships = userMemberships?.revalidate;
  useEffect(() => {
    void revalidateMemberships?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, isLoaded]);

  const memberships = userMemberships?.data ?? [];
  const total = Math.max(userMemberships?.count ?? 0, memberships.length);
  if (!isLoaded || total < 2) return null;

  const choose = async (id: string) => {
    if (id === activeId) {
      onSwitched();
      return;
    }
    setSwitchingId(id);
    try {
      await setActive({ organization: id });
      onSwitched();
    } catch {
      toast.error('Could not switch organization');
    } finally {
      setSwitchingId(null);
    }
  };

  return (
    <>
      <UserMenuRow
        isDark={isDark}
        label='Switch organization'
        expanded={expanded}
        onClick={() => onExpandedChange(!expanded)}
        icon={
          <DynamicIcon
            name='buildings'
            weight='solid'
            size={16}
            className={cn(isDark && 'text-slate-400')}
          />
        }
        trailing={
          <DynamicIcon
            name={expanded ? 'chevron-down' : 'chevron-right'}
            size={10}
            className={cn(
              'ml-auto shrink-0',
              isDark ? 'text-slate-500' : 'text-tertiary-foreground'
            )}
          />
        }
      />
      {expanded && (
        <div
          role='group'
          aria-label='Organizations'
          className='max-h-60 overflow-y-auto'
        >
          {memberships.map(({ organization: org }) => {
            const active = org.id === activeId;
            return (
              <button
                key={org.id}
                type='button'
                aria-current={active ? 'true' : undefined}
                disabled={switchingId !== null}
                onClick={() => void choose(org.id)}
                className={cn(
                  menuRowClass(isDark),
                  'pl-9 disabled:cursor-default disabled:opacity-60',
                  active && 'font-medium text-foreground'
                )}
              >
                <OrganizationMark name={org.name} imageUrl={org.imageUrl} />
                <span className='truncate' title={org.name}>
                  {org.name}
                </span>
                {switchingId === org.id ? (
                  <AppIcon
                    icon={iconSpinnerThird}
                    className='ml-auto shrink-0 animate-spin text-muted-foreground'
                    style={{ fontSize: 12 }}
                  />
                ) : (
                  active && (
                    <DynamicIcon
                      name='check'
                      size={12}
                      className='ml-auto shrink-0 text-primary'
                    />
                  )
                )}
              </button>
            );
          })}
          {userMemberships?.hasNextPage && (
            <button
              type='button'
              disabled={userMemberships.isFetching}
              onClick={() => userMemberships.fetchNext?.()}
              className={cn(menuRowClass(isDark), 'pl-9')}
            >
              Show more
            </button>
          )}
        </div>
      )}
    </>
  );
}

/** An invitation's or a suggestion's action: Join, or Request to join. */
function OfferAction({
  label,
  organization,
  busy,
  disabled,
  isDark,
  onClick,
}: {
  label: string;
  organization: string;
  busy: boolean;
  disabled: boolean;
  isDark: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type='button'
      aria-label={`${label} ${organization}`}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'ml-auto inline-flex h-6 shrink-0 items-center gap-1.5 rounded-md border px-2 text-xs font-medium transition-colors disabled:cursor-default disabled:opacity-60',
        isDark
          ? 'border-white/10 text-slate-200 hover:bg-white/[0.08]'
          : 'border-black/10 text-foreground hover:bg-black/[0.05]'
      )}
    >
      {busy && (
        <AppIcon
          icon={iconSpinnerThird}
          className='animate-spin'
          style={{ fontSize: 10 }}
        />
      )}
      {label}
    </button>
  );
}

/**
 * The invitations and suggestions Clerk's switcher listed above the
 * organizations, read with its params: an invitation is joined at once, a
 * suggestion is a request an admin approves, pending until then. Hidden when
 * there are none.
 */
function OrganizationInvitations({
  isDark,
  onJoined,
}: {
  isDark: boolean;
  onJoined: () => void;
}) {
  const { isLoaded, userMemberships, userInvitations, userSuggestions } =
    useOrganizationList({
      userMemberships: { infinite: true },
      userInvitations: { infinite: true },
      userSuggestions: { infinite: true, status: ['pending', 'accepted'] },
    });
  const [busyId, setBusyId] = useState<string | null>(null);

  const invitations = (userInvitations?.data ?? []).filter(
    (invitation) => invitation.status === 'pending'
  );
  const suggestions = userSuggestions?.data ?? [];
  if (!isLoaded || invitations.length + suggestions.length === 0) return null;

  const accept = async (
    offer: UserOrganizationInvitationResource | OrganizationSuggestionResource,
    failure: string,
    reload: () => Promise<unknown>
  ) => {
    setBusyId(offer.id);
    try {
      await offer.accept();
    } catch {
      toast.error(failure);
      setBusyId(null);
      return false;
    }
    await reload().catch(() => undefined);
    setBusyId(null);
    return true;
  };

  const join = async (invitation: UserOrganizationInvitationResource) => {
    const { name } = invitation.publicOrganizationData;
    const joined = await accept(invitation, `Could not join ${name}`, () =>
      Promise.all([
        userInvitations?.revalidate?.(),
        userMemberships?.revalidate?.(),
      ])
    );
    if (joined) onJoined();
  };

  const request = (suggestion: OrganizationSuggestionResource) =>
    accept(
      suggestion,
      `Could not request to join ${suggestion.publicOrganizationData.name}`,
      async () => userSuggestions?.revalidate?.()
    );

  const row = (
    id: string,
    { name, imageUrl }: { name: string; imageUrl: string },
    action: ReactNode
  ) => (
    <div
      key={id}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap',
        isDark ? 'text-slate-300' : 'text-muted-foreground'
      )}
    >
      <span className='flex w-4 shrink-0 items-center justify-center'>
        <OrganizationMark name={name} imageUrl={imageUrl} />
      </span>
      <span className='min-w-0 truncate' title={name}>
        {name}
      </span>
      {action}
    </div>
  );

  return (
    <div role='group' aria-label='Organizations to join'>
      {invitations.map((invitation) =>
        row(
          invitation.id,
          invitation.publicOrganizationData,
          <OfferAction
            label='Join'
            organization={invitation.publicOrganizationData.name}
            busy={busyId === invitation.id}
            disabled={busyId !== null}
            isDark={isDark}
            onClick={() => void join(invitation)}
          />
        )
      )}
      {suggestions.map((suggestion) =>
        row(
          suggestion.id,
          suggestion.publicOrganizationData,
          suggestion.status === 'accepted' ? (
            <span className='ml-auto shrink-0 text-xs text-tertiary-foreground'>
              Pending approval
            </span>
          ) : (
            <OfferAction
              label='Request to join'
              organization={suggestion.publicOrganizationData.name}
              busy={busyId === suggestion.id}
              disabled={busyId !== null}
              isDark={isDark}
              onClick={() => void request(suggestion)}
            />
          )
        )
      )}
    </div>
  );
}

type UserMenuProps = {
  /** Custom trigger element (the Arc sidebar's settings gear). */
  trigger?: ReactElement;
};

/**
 * The trigger is the avatar with the name and the org under it, sized for the
 * sidebar, or the avatar alone when the sidebar is folded.
 */
export function UserMenu({ trigger }: UserMenuProps = {}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const clerk = useClerk();
  const { user } = useUser();
  const { organization } = useOrganization();
  const { isCollapsed } = useSidebar();
  const [open, setOpen] = useState(false);

  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.primaryEmailAddress?.emailAddress ||
    'User';
  const orgName = organization?.name || '';

  const close = () => setOpen(false);

  // Theme-adaptive weight: menu icons render solid (filled) in dark mode and
  // regular (outline) in light mode. DynamicIcon resolves weight='solid'
  // per-theme (solid in dark, outline in light); every icon used below has a
  // solid variant in FILLED_MAP so the fill is consistent across the menu.
  const weight = 'solid';

  // The folded sidebar shows the avatar alone, named by a tooltip.
  const avatarOnly = isCollapsed && !trigger;
  const popoverTrigger = (
    <PopoverTrigger asChild>
      {trigger ?? (
        <button
          type='button'
          aria-label='Account menu'
          className={cn(
            'flex items-center transition-colors duration-150',
            avatarOnly
              ? 'h-7 w-10 justify-center rounded-lg'
              : 'h-9 min-w-0 flex-1 gap-2 rounded-lg px-1.5 text-left',
            isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]'
          )}
        >
          <span
            className={cn(
              'flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted',
              avatarOnly ? 'h-[22px] w-[22px]' : 'h-7 w-7'
            )}
          >
            {user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={fullName}
                className='h-full w-full rounded-full object-cover'
              />
            ) : (
              <AppIcon
                icon={iconUser}
                className='text-muted-foreground'
                style={{ fontSize: avatarOnly ? 11 : 13 }}
              />
            )}
          </span>
          {!avatarOnly && (
            <span className='flex min-w-0 flex-1 flex-col'>
              <span
                title={fullName}
                className='truncate text-[13px] leading-4 font-medium text-foreground'
              >
                {fullName}
              </span>
              {orgName && (
                <span
                  title={orgName}
                  className='truncate text-[11px] leading-[14px] text-tertiary-foreground'
                >
                  {orgName}
                </span>
              )}
            </span>
          )}
        </button>
      )}
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {avatarOnly ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{popoverTrigger}</TooltipTrigger>
          <TooltipContent side='right' sideOffset={8}>
            <span className='block'>{fullName}</span>
            {orgName && <span className='block opacity-70'>{orgName}</span>}
          </TooltipContent>
        </Tooltip>
      ) : (
        popoverTrigger
      )}
      <PopoverContent
        side='right'
        align='end'
        sideOffset={10}
        className={cn(
          'min-w-[18rem] p-1.5',
          // Room for the organization list on short windows.
          'max-h-[var(--radix-popover-content-available-height)] overflow-y-auto',
          isDark ? 'border-white/6! bg-[#1a1a1a]! text-slate-200' : 'bg-popover'
        )}
      >
        {/* Identity header — avatar + name/org, mirrors the top-bar user area */}
        <div
          className={cn(
            'mb-1 flex items-center gap-2.5 rounded-md px-2.5 py-2',
            isDark ? 'bg-white/[0.03]' : 'bg-black/2'
          )}
        >
          <span className='flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted'>
            {user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={fullName}
                className='h-full w-full rounded-full object-cover'
              />
            ) : (
              <AppIcon
                icon={iconUser}
                className='text-muted-foreground'
                style={{ fontSize: 15 }}
              />
            )}
          </span>
          <span className='flex min-w-0 flex-col'>
            <span className='truncate text-sm leading-tight font-semibold text-foreground'>
              {fullName}
            </span>
            {orgName && (
              <span className='truncate text-xs leading-tight text-tertiary-foreground'>
                {orgName}
              </span>
            )}
          </span>
          {/* App version — relocated here from the sidebar brand block. */}
          <span
            className={cn(
              'ml-auto self-start rounded-md px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap',
              isDark
                ? 'bg-white/[0.05] text-slate-400'
                : 'bg-black/4 text-tertiary-foreground'
            )}
          >
            v{__APP_VERSION__}
          </span>
        </div>

        {/* Settings holds the account, organization, appearance, API key,
            billing and staff rows; the menu links to it, as the gear beside
            the user does, and keeps only what Settings lacks. */}
        <OrganizationSection
          activeId={organization?.id}
          isDark={isDark}
          onSwitched={close}
        />

        <UserMenuRow
          isDark={isDark}
          label='Settings'
          to='/settings'
          onClick={close}
          icon={<DynamicIcon name='gear' weight={weight} className='h-4 w-4' />}
        />

        {/* Resources & links. */}
        <UserMenuRow
          isDark={isDark}
          label='Extension'
          to='/extension'
          onClick={close}
          icon={
            <DynamicIcon name='browser' weight={weight} className='h-4 w-4' />
          }
        />

        <UserMenuRow
          isDark={isDark}
          label='Resources'
          to='https://docs.cleanlist.ai'
          external
          onClick={close}
          icon={<DynamicIcon name='book' weight={weight} className='h-4 w-4' />}
        />

        <UserMenuRow
          isDark={isDark}
          label='Book a call'
          to='https://calendly.com/d/d3tq-wdx-sjp'
          external
          onClick={close}
          icon={
            <DynamicIcon name='calendar' weight={weight} className='h-4 w-4' />
          }
        />

        <UserMenuRow
          isDark={isDark}
          label='Give feedback'
          to='https://calendly.com/d/d3tq-wdx-sjp'
          external
          onClick={close}
          icon={
            <DynamicIcon
              name='message-plus'
              weight={weight}
              className='h-4 w-4'
            />
          }
        />

        <div
          className={cn('my-1 h-px', isDark ? 'bg-white/[0.06]' : 'bg-black/6')}
        />

        <UserMenuRow
          isDark={isDark}
          label='Log out'
          icon={
            <DynamicIcon
              name='arrow-right-from-bracket'
              weight={weight}
              size={16}
            />
          }
          onClick={() => {
            close();
            clerk.signOut();
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
